import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AnimalSymbol from "@/components/AnimalSymbol";
import PedigreeTree from "@/components/PedigreeTree";
import PhotoUploader from "@/components/PhotoUploader";
import PhotoGallery from "@/components/PhotoGallery";
import MeasurementsLog from "@/components/MeasurementsLog";
import AutocompleteInput from "@/components/AutocompleteInput";
import AnalysisPanel from "@/components/AnalysisPanel";
import { QUALITIES, BODY_PARTS, emptyParts } from "@/lib/quality";
import { CONDITION_FIELDS } from "@/lib/conditions";
import { formatUS } from "@/lib/dates";
import { computeCOI } from "@/lib/coi";
import { ChevronLeft, Save, Trash2, Share2, Printer, Copy, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnimalPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [animal, setAnimal] = useState(null);
  const [animals, setAnimals] = useState([]);
  const [pedigree, setPedigree] = useState(null);
  const [progeny, setProgeny] = useState([]);
  const [descendants, setDescendants] = useState([]);
  const [descendantsGens, setDescendantsGens] = useState(3);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pedigreeGens, setPedigreeGens] = useState(4);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareRotating, setShareRotating] = useState(false);

  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, all] = await Promise.all([
        axios.get(`${API}/animals/${id}`),
        axios.get(`${API}/animals`),
      ]);
      setAnimal(a.data);
      setAnimals(all.data);
      setDirty(false);
      setNotFound(false);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else throw err;
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadPedigree = useCallback(async () => {
    if (!animal) return;
    const r = await axios.get(`${API}/animals/${id}/pedigree?generations=${pedigreeGens}`);
    setPedigree(r.data);
  }, [animal, id, pedigreeGens]);

  const loadProgeny = useCallback(async () => {
    if (!animal) return;
    const r = await axios.get(`${API}/animals/${id}/progeny`);
    setProgeny(r.data);
  }, [animal, id]);

  const loadDescendants = useCallback(async () => {
    if (!animal) return;
    const r = await axios.get(`${API}/animals/${id}/descendants?generations=${descendantsGens}`);
    setDescendants(r.data?.descendants || []);
  }, [animal, id, descendantsGens]);

  useEffect(() => { loadPedigree(); }, [loadPedigree]);
  useEffect(() => { loadProgeny(); }, [loadProgeny]);
  useEffect(() => { loadDescendants(); }, [loadDescendants]);

  const update = (patch) => { setAnimal({ ...animal, ...patch }); setDirty(true); };
  const updatePart = (k, v) => { setAnimal({ ...animal, parts: { ...(animal.parts || {}), [k]: v } }); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { id: _i, share_token, created_at, updated_at, ...rest } = animal;
      const r = await axios.put(`${API}/animals/${id}`, rest);
      setAnimal(r.data); setDirty(false);
      // Refresh the cached pedigree so the Analysis tab + repeated-condition
      // chips pick up any health / structure changes immediately.
      loadPedigree();
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const del = async () => {
    try {
      await axios.delete(`${API}/animals/${id}`);
      toast.success("Animal deleted");
      nav("/");
    } catch { toast.error("Delete failed"); }
  };

  const shareUrl = animal ? `${window.location.origin}/share/${animal.share_token}` : "";
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const rotateShare = async () => {
    setShareRotating(true);
    try {
      const r = await axios.post(`${API}/animals/${id}/share`);
      setAnimal(r.data);
      toast.success("New share link generated");
    } catch { toast.error("Couldn't rotate link"); }
    setShareRotating(false);
  };
  const openPrint = (template, gen = 3) => {
    window.open(`/print/${id}?template=${template}&gen=${gen}`, "_blank", "noopener,noreferrer");
  };

  const ancestorsKeyed = useMemo(() => {
    if (!pedigree) return {};
    const out = {};
    Object.entries(pedigree.ancestors).forEach(([k, v]) => { out[parseInt(k, 10)] = v; });
    return out;
  }, [pedigree]);

  // Wright's Coefficient of Inbreeding for the loaded pedigree depth.
  const coi = useMemo(() => computeCOI(ancestorsKeyed), [ancestorsKeyed]);

  const males = useMemo(() => animals.filter((a) => a.sex === "male" && a.id !== id), [animals, id]);
  const females = useMemo(() => animals.filter((a) => a.sex === "female" && a.id !== id), [animals, id]);

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="animal-not-found">
      <div className="text-center max-w-md">
        <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">Vitality Pedigree</div>
        <div className="font-display font-extrabold text-2xl mb-2">Animal not found</div>
        <p className="text-sm text-[var(--app-text-soft)] mb-5">
          This animal doesn't exist or was deleted. It may have belonged to another account.
        </p>
        <button onClick={() => nav("/")} className="text-sm text-blue-600 hover:underline">← Back to Library</button>
      </div>
    </div>
  );
  if (!animal) return <div className="p-16 text-center" data-testid="animal-loading">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--app-bg)] flex flex-col">
      <Toaster richColors position="top-right" />
      <header className="h-14 border-b border-[var(--app-border)] bg-white px-5 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/")} data-testid="back-library">
          <ChevronLeft className="w-4 h-4 mr-1" /> Library
        </Button>
        <div className="w-px h-7 bg-[var(--app-border)]" />
        <div className="leading-tight">
          <div className="font-display font-extrabold text-lg tracking-tight" data-testid="animal-name-header">{animal.name}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--app-text-soft)]">{animal.species_or_breed || "Animal"} · {animal.sex}</div>
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={() => setShareOpen(true)} data-testid="btn-share-animal">
          <Share2 className="w-4 h-4 mr-1.5" /> Share
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="btn-print-animal">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => openPrint("akc", 3)} data-testid="print-3gen-safari">
              <div className="flex flex-col">
                <span className="text-sm font-display font-bold">3 generations</span>
                <span className="text-[10px] text-[var(--app-text-soft)]">Readable on Safari · one-page landscape</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openPrint("akc", 5)} data-testid="print-5gen-chrome">
              <div className="flex flex-col">
                <span className="text-sm font-display font-bold">5 generations</span>
                <span className="text-[10px] text-[var(--app-text-soft)]">Dense · best in Chrome / Firefox</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="ghost" size="sm" onClick={() => setPendingDelete(true)} className="text-red-600 hover:text-red-700" data-testid="btn-delete-animal">
          <Trash2 className="w-4 h-4 mr-1.5" /> Delete
        </Button>
        <Button size="sm" disabled={!dirty || saving} onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-save">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </Button>
      </header>

      <Tabs defaultValue="details" className="flex-1 flex flex-col">
        <TabsList className="mx-6 mt-4 grid grid-cols-5 max-w-3xl" data-testid="animal-tabs">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="structure" data-testid="tab-structure">Structure</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health">Health</TabsTrigger>
          <TabsTrigger value="pedigree" data-testid="tab-pedigree">Pedigree</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-analysis">Analysis</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <TabsContent value="details" className="m-0 max-w-3xl space-y-4">
            <div className="grid grid-cols-[180px_1fr] gap-5 items-start">
              <PhotoUploader photoPath={animal.photo_path || ""} onChange={(p) => update({ photo_path: p })} animalId={id} />
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Name</Label>
                    <Input value={animal.name} onChange={(e) => update({ name: e.target.value })} data-testid="field-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Sex</Label>
                    <Select value={animal.sex} onValueChange={(v) => update({ sex: v })}>
                      <SelectTrigger data-testid="field-sex"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Breed or species (optional)</Label>
                  <AutocompleteInput field="species_or_breed" value={animal.species_or_breed || ""} onChange={(v) => update({ species_or_breed: v })} testid="field-species-or-breed" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Registration #</Label>
                    <Input className="font-mono-tech" value={animal.registration || ""} onChange={(e) => update({ registration: e.target.value })} data-testid="field-registration" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Title</Label>
                    <AutocompleteInput field="title" value={animal.title || ""} onChange={(v) => update({ title: v })} testid="field-title" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Date of birth</Label>
                  <Input type="date" className="font-mono-tech max-w-[200px]" value={animal.dob || ""} onChange={(e) => update({ dob: e.target.value })} data-testid="field-dob" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Sire</Label>
                    <Select value={animal.sire_id || "none"} onValueChange={(v) => update({ sire_id: v === "none" ? null : v })}>
                      <SelectTrigger data-testid="field-sire"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {males.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Dam</Label>
                    <Select value={animal.dam_id || "none"} onValueChange={(v) => update({ dam_id: v === "none" ? null : v })}>
                      <SelectTrigger data-testid="field-dam"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {females.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Notes</Label>
                  <Textarea rows={3} value={animal.notes || ""} onChange={(e) => update({ notes: e.target.value })} data-testid="field-notes" />
                </div>
              </div>
            </div>

            {/* Photo gallery */}
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">Photo gallery</div>
              <PhotoGallery
                animalId={id}
                primary={animal.photo_path || ""}
                paths={animal.photo_paths || []}
                onChange={({ primary, paths }) => setAnimal((cur) => ({ ...cur, photo_path: primary, photo_paths: paths }))}
              />
            </div>

            {/* Height & weight log */}
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">Height &amp; weight</div>
              <MeasurementsLog
                animalId={id}
                measurements={animal.measurements || []}
                onChange={(next) => setAnimal((cur) => ({ ...cur, measurements: next }))}
              />
            </div>

            {/* Progeny */}
            <div className="pt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">Progeny ({progeny.length})</div>
              {progeny.length === 0 ? (
                <div className="text-sm text-[var(--app-text-soft)] py-3">No progeny recorded yet.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="progeny-list">
                  {progeny.map((p) => (
                    <Link key={p.id} to={`/animals/${p.id}`} className="bg-white border border-[var(--app-border)] rounded p-2 flex gap-2 items-center hover:shadow-sm" data-testid={`progeny-${p.id}`}>
                      <AnimalSymbol parts={p.parts} size={36} />
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-sm truncate">{p.name}</div>
                        <div className="text-[10px] text-[var(--app-text-soft)] truncate">{p.sex} · {formatUS(p.dob) || "—"}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="structure" className="m-0 max-w-2xl space-y-4">
            <div className="flex items-center gap-4 bg-white border border-[var(--app-border)] rounded p-4">
              <AnimalSymbol parts={animal.parts || emptyParts()} size={140} />
              <p className="text-xs text-[var(--app-text-soft)]">Assign a quality grade to each of the 7 structural parts.</p>
            </div>
            <div className="space-y-2 max-w-md">
              {BODY_PARTS.map((bp) => (
                <div key={bp.key} className="flex items-center gap-3" data-testid={`part-row-${bp.key}`}>
                  <Label className="w-20 text-xs uppercase tracking-wider text-[var(--app-text-soft)]">{bp.label}</Label>
                  <Select value={(animal.parts || {})[bp.key] || "unknown"} onValueChange={(v) => updatePart(bp.key, v)}>
                    <SelectTrigger className="flex-1" data-testid={`part-select-${bp.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {QUALITIES.map((q) => (
                        <SelectItem key={q.value} value={q.value}>
                          <div className="flex items-center gap-2">
                            <span className="w-3.5 h-3.5 rounded-sm border border-black/10" style={{ backgroundColor: q.color }} />
                            <span>{q.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="health" className="m-0 max-w-2xl space-y-4">
            <p className="text-xs text-[var(--app-text-soft)]">
              Separate multiple entries with commas. Suggestions appear from your previously entered values.
            </p>
            {CONDITION_FIELDS.map((f) => (
              <div key={f.key} data-testid={`health-row-${f.key}`}>
                <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)] flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full border border-black/15" style={{ backgroundColor: f.color }} />
                  {f.label}
                </Label>
                <AutocompleteInput field={f.key} multi value={animal[f.key] || ""} onChange={(v) => update({ [f.key]: v })} placeholder={f.placeholder} testid={`field-${f.key}`} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="pedigree" className="m-0">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Generations</Label>
              <Select value={String(pedigreeGens)} onValueChange={(v) => setPedigreeGens(parseInt(v, 10))}>
                <SelectTrigger className="w-[80px]" data-testid="pedigree-gens"><SelectValue /></SelectTrigger>
                <SelectContent>{[4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <div
                className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded border border-[var(--app-border)] bg-white"
                data-testid="coi-badge"
                title="Wright's Coefficient of Inbreeding across the loaded generations"
              >
                <span className="text-[10px] uppercase tracking-[0.18em] font-display font-semibold text-[var(--app-text-soft)]">COI</span>
                <span className="font-mono-tech font-bold text-sm" data-testid="coi-badge-value">{(coi.coi * 100).toFixed(2)}%</span>
                <span className="text-[10px] text-[var(--app-text-soft)] font-mono-tech">· {pedigreeGens} gen</span>
              </div>
              <div className="text-xs text-[var(--app-text-soft)]">Tree is built from each animal's sire/dam pointers.</div>
              <div className="flex-1" />
              <a
                href={`${API}/exports/animals/${id}/pedigree.csv?generations=${pedigreeGens}`}
                download={`${(animal?.name || "pedigree").replace(/\s+/g, "_")}-pedigree-${pedigreeGens}gen.csv`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm" type="button" data-testid="download-pedigree-csv">
                  <Download className="w-4 h-4 mr-1.5" /> Download CSV
                </Button>
              </a>
            </div>
            <div className="bg-white border border-[var(--app-border)] rounded p-4 overflow-auto" style={{ maxHeight: "70vh" }}>
              {pedigree && <PedigreeTree generations={pedigree.generations} ancestors={ancestorsKeyed} selectedIndex={null} onSelect={(i) => { const a = ancestorsKeyed[i]; if (a?.id && a.id !== id) nav(`/animals/${a.id}`); }} />}
            </div>
          </TabsContent>

          <TabsContent value="analysis" className="m-0 max-w-4xl">
            <AnalysisPanel
              pedigree={pedigree}
              ancestorsKeyed={ancestorsKeyed}
              descendants={descendants}
              descendantsGenerations={descendantsGens}
              onDescendantsGenerationsChange={setDescendantsGens}
            />
          </TabsContent>
        </div>
      </Tabs>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl">Share read-only pedigree</DialogTitle>
            <DialogDescription>
              Anyone with this link can view {animal.name}'s details, structure, health and 4-generation pedigree. They cannot edit anything. Rotate the link any time to revoke previous access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={shareUrl} className="font-mono-tech text-xs" data-testid="share-url" />
              <Button onClick={copyShare} variant="outline" data-testid="copy-share">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <div className="flex justify-between items-center">
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline" data-testid="open-share">Open in new tab →</a>
              <Button variant="ghost" size="sm" onClick={rotateShare} disabled={shareRotating} data-testid="rotate-share">
                {shareRotating ? "Rotating…" : "Rotate link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingDelete} onOpenChange={setPendingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {animal.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the animal. Any other animal listing it as a parent will have that reference cleared.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={del} data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
